/**
 * Einzelheiten zu einem Vorgang des Kanalmanagers — der empfangene oder gesendete Inhalt.
 *
 * Lädt den Rohinhalt erst beim Öffnen nach und zeigt ihn lesbar formatiert mit einer
 * Kopieren-Aktion, damit er sich an den Vendor-Support weiterreichen lässt.
 */
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '@/shared/components';
import { useApi, type SyncActivityDetails, type SyncActivityEvent } from '@/shared/api';
import { formatDateTime } from '@/shared/lib/format';
import { SyncActivityPayloadBlock } from './SyncActivityPayloadBlock';
import { syncEventLabel, syncReferenceLabel, syncStatusLabel } from './syncActivityLabels';

interface Props {
  event: SyncActivityEvent | null;
  onClose: () => void;
}

export function SyncActivityDetailsDrawer({ event, onClose }: Props) {
  const api = useApi();
  const open = event !== null;
  const kind = event?.referenceKind === 'audit' ? 'audit' : 'webhook';

  const { data, isLoading, isError } = useQuery({
    queryKey: ['channex', 'syncActivityDetails', event?.referenceKind, event?.referenceId],
    queryFn: () => api.channex.syncActivityDetails(kind, event!.referenceId),
    enabled: open,
    staleTime: 60_000,
  });

  const payloadPretty = formatJson(data?.payload ?? null);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{event ? syncEventLabel(event.eventType) : ''}</DialogTitle>
          <p className="text-ink-soft mt-1 text-xs">
            {syncReferenceLabel(event?.referenceKind)}
            {event && ` · ${event.eventType}`}
          </p>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {isLoading && (
            <p className="text-ink-soft text-sm" role="status">
              Einzelheiten werden geladen…
            </p>
          )}
          {isError && (
            <p className="text-sealwax text-sm">Die Einzelheiten konnten nicht geladen werden.</p>
          )}
          {data && (
            <>
              <MetaGrid data={data} />
              {data.error && (
                <div>
                  <div className="text-ink-faint mb-1.5 text-[10px] uppercase tracking-wider">
                    Grund des Scheiterns
                  </div>
                  <pre className="border-line/40 bg-card-alt/40 text-sealwax overflow-x-auto rounded border p-3 text-xs">
                    {data.error}
                  </pre>
                </div>
              )}
              {data.payload && (
                <SyncActivityPayloadBlock raw={data.payload} formatted={payloadPretty} />
              )}
            </>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

function MetaGrid({ data }: { data: SyncActivityDetails }) {
  return (
    <dl className="border-line/40 grid grid-cols-1 gap-x-6 gap-y-2 rounded border p-4 text-xs sm:grid-cols-2">
      <Row label="Zustand" value={syncStatusLabel(data.status)} />
      <Row label="Eingetroffen" value={formatDateTime(data.occurredAt)} />
      {data.processedAt && <Row label="Verarbeitet" value={formatDateTime(data.processedAt)} />}
      {data.attempts !== null && <Row label="Versuche" value={String(data.attempts)} />}
      {data.liveFeedEventId && (
        <Row label="Kennung der Meldung" value={data.liveFeedEventId} mono />
      )}
      <Row label="Kennung des Vorgangs" value={data.referenceId} mono />
    </dl>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <>
      <dt className="text-ink-faint uppercase tracking-wider">{label}</dt>
      <dd className={mono ? 'text-ink wrap-break-word font-mono' : 'text-ink wrap-break-word'}>
        {value}
      </dd>
    </>
  );
}

/** Kein Hook, nur eine Formatierung — der Name sagte etwas anderes. */
function formatJson(payload: string | null): string {
  if (!payload) return '';
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    return payload;
  }
}
